/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer, Observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { TrainingProgressIndicator } from 'src/modules/modelTraining/components/TrainingProgressIndicator';
import { TrainingJobStore } from 'src/modules/modelTraining/stores/TrainingJobInfoStore';

export type TrainingJobTableProps = TableProps<TrainingJobStore> & {
    trainedModelNames: string[];
    onModelClick: (modelId: string) => void;
    onTrainingJobClick: (jobId: string) => void;
};

const messages = defineMessages({
    jobId: {
        id: 'trainingJobTable.trainingJobTable.jobId',
        defaultMessage: 'Training job id'
    },
    status: {
        id: 'trainingJobTable.trainingJobTable.status',
        defaultMessage: 'Status'
    },
    outputModel: {
        id: 'trainingJobTable.trainingJobTable.outputModel',
        defaultMessage: 'Output model'
    },
    submitted: {
        id: 'trainingJobTable.trainingJobTable.submitted',
        defaultMessage: 'Submitted'
    },
    trainingSucceeded: {
        id: 'trainingJobTable.trainingJobTable.trainingSucceeded',
        defaultMessage: 'Training succeeded'
    },
    trainingFailed: {
        id: 'trainingJobTable.trainingJobTable.trainingFailed',
        defaultMessage: 'Training job failed'
    },
    trainingCancelling: {
        id: 'trainingJobTable.trainingJobTable.trainingCancelling',
        defaultMessage: 'Cancelling...'
    },
    trainingCancelled: {
        id: 'trainingJobTable.trainingJobTable.trainingCancelled',
        defaultMessage: 'Training cancelled'
    },
    training: {
        id: 'trainingJobTable.trainingJobTable.training',
        defaultMessage: 'Training'
    },
    complete: {
        id: 'trainingJobTable.trainingJobTable.complete',
        defaultMessage: 'complete'
    },
    trainingComplete: {
        id: 'trainingJobTable.trainingJobTable.trainingComplete',
        defaultMessage: 'Training completed.'
    },
    evaluation: {
        id: 'trainingJobTable.trainingJobTable.evaluation',
        defaultMessage: 'Evaluation'
    },
    jobNotStartedDescription: {
        id: 'trainingJobTable.trainingJobTable.jobNotStartedDescription',
        defaultMessage: 'Job in queue. Starting soon ... '
    },
    emptyTablePlaceholder: {
        id: 'trainingJobTable.trainingJobTable.emptyTablePlaceholder',
        defaultMessage: `No items found.`
    },
    deletedModel: {
        id: 'trainingJobTable.trainingJobTable.deletedModel',
        defaultMessage: `This model was deleted`
    }
});

const theme = getScopedTheme('TrainingJobTable');

export const TrainingJobTable = observer((props: TrainingJobTableProps) => {
    const { trainedModelNames, sortKey, onModelClick, onTrainingJobClick, ...rest } = props;
    const { localize } = useLocalization();

    const onRenderStatus = (item: TrainingJobStore, _index: number, column: IColumn) => {
        switch (item.status) {
            case 'succeeded':
                return (
                    <Stack horizontal verticalAlign="baseline" tokens={{ childrenGap: 4 }}>
                        <FontIcon iconName="Completed" style={{ color: theme.successColor }} />
                        <LuisText>{localize(messages.trainingSucceeded)}</LuisText>
                    </Stack>
                );
            case 'cancelling':
                return <LuisText>{localize(messages.trainingCancelling)}</LuisText>;
            case 'cancelled':
                return (
                    <Stack horizontal verticalAlign="baseline" tokens={{ childrenGap: 4 }}>
                        <FontIcon iconName="Cancel" style={{ color: theme.cancelColor }} />
                        <LuisText>{localize(messages.trainingCancelled)}</LuisText>
                    </Stack>
                );
            case 'notStarted':
            case 'inProgress':
                return <TrainingProgressIndicator trainingJob={item} maxWidth={column.calculatedWidth} />;
            case 'failed':
            default:
                return (
                    <Stack horizontal verticalAlign="baseline" tokens={{ childrenGap: 4 }}>
                        <FontIcon iconName="Warning" style={{ color: theme.failureColor }} />
                        <LuisText>{localize(messages.trainingFailed)}</LuisText>
                    </Stack>
                );
        }
    };

    const onRenderOutputModel = (item: TrainingJobStore) => {
        const isDeletedModel = !trainedModelNames.find(m => m === item.modelId);
        const disabledModel = item.status !== 'succeeded' || !item.evaluationStatus?.percentComplete;

        return (
            <Observer>
                {() => (
                    <NameCellLink onClick={() => onModelClick(item.modelId)} disabled={disabledModel || isDeletedModel}>
                        <LuisTooltip hidden={disabledModel || !isDeletedModel} content={localize(messages.deletedModel)}>
                            {item.modelId}
                        </LuisTooltip>
                    </NameCellLink>
                )}
            </Observer>
        );
    };

    const columns: IColumn[] = [
        {
            key: 'jobId',
            minWidth: 150,
            maxWidth: 300,
            fieldName: 'id',
            isRowHeader: true,
            isResizable: true,
            name: localize(messages.jobId),
            isSorted: sortKey && !!(sortKey.key === 'id'),
            isSortedDescending: sortKey && !!(sortKey.key === 'id') && sortKey?.order === 'desc',
            onRender: (item: TrainingJobStore) => <NameCellLink onClick={() => onTrainingJobClick(item.id)}>{item.id ?? '--'}</NameCellLink>
        },
        {
            key: 'status',
            minWidth: 150,
            maxWidth: 300,
            isResizable: true,
            fieldName: 'percentComplete',
            onRender: (item, index, column) => <Observer>{() => onRenderStatus(item, index, column)}</Observer>,
            name: localize(messages.status),
            isSorted: sortKey && !!(sortKey.key === 'percentComplete'),
            isSortedDescending: sortKey && !!(sortKey.key === 'percentComplete') && sortKey?.order === 'desc'
        },
        {
            key: 'outputModel',
            minWidth: 150,
            maxWidth: 300,
            isResizable: true,
            fieldName: 'modelId',
            name: localize(messages.outputModel),
            isSorted: sortKey && !!(sortKey.key === 'modelId'),
            isSortedDescending: sortKey && !!(sortKey.key === 'modelId') && sortKey?.order === 'desc',
            onRender: onRenderOutputModel
        },
        {
            key: 'submitted',
            minWidth: 150,
            maxWidth: 300,
            isResizable: true,
            fieldName: 'startDateTime',
            name: localize(messages.submitted),
            isSorted: sortKey && !!(sortKey.key === 'startDateTime'),
            isSortedDescending: sortKey && !!(sortKey.key === 'startDateTime') && sortKey?.order === 'desc',
            onRender: (item: TrainingJobStore) => <LuisText>{item.startDateTime?.toLocaleDateString() ?? '--'}</LuisText>
        }
    ];

    return (
        <Table<TrainingJobStore>
            columns={columns}
            selectionMode={SelectionMode.single}
            emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
            {...rest}
        />
    );
});
